import {
  Building2,
  Calendar,
  Globe,
  Hash,
  Infinity as InfinityIcon,
  Mail,
  Users,
  Zap,
} from "lucide-react";
import type * as React from "react";

import type { AdminOrganization } from "@ando/core";
import { Badge } from "@ando/ui";

import {
  useAdminOrgConnectors,
  useAdminOrgMembers,
  useAdminOrgUsage,
} from "@/features/admin/hooks/useAdminOrg";

import { OrgSetupChecklist } from "./OrgSetupChecklist";

/**
 * Overview tab — KPI grid (members / connectors / multiplier / created)
 * + canonical facts card (slug, billing email, tier, deployment mode,
 * timestamps).
 *
 * v1 reference: ``OrgOverviewTab.tsx`` (122 LoC). The v2 surface ALSO
 * triggers ``useAdminOrgUsage`` to feed the cost-cap KPI when available
 * (graceful fallback when the org has no billing subscription).
 */

export interface OrgOverviewTabProps {
  org: AdminOrganization;
}

function StatCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="rounded-lg border border-hairline bg-card/55 p-3.5">
      <div className="flex items-center gap-2">
        <div
          className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-ando-orange-soft/40 text-accent"
          aria-hidden
        >
          {icon}
        </div>
        <div className="text-[10px] text-ink-3 uppercase tracking-wide">{label}</div>
      </div>
      <div className="mt-2 font-display font-semibold text-[20px] text-ink-1 tabular-nums">
        {value}
      </div>
      {hint ? <div className="mt-0.5 text-[11px] text-ink-3 tabular-nums">{hint}</div> : null}
    </div>
  );
}

function Field({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
}): React.ReactElement {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-[11px] text-ink-3 uppercase tracking-wide">
        {icon}
        {label}
      </dt>
      <dd className="mt-1 text-[13px] text-ink-1">{value}</dd>
    </div>
  );
}

export function OrgOverviewTab({ org }: OrgOverviewTabProps): React.ReactElement {
  const members = useAdminOrgMembers(org.id);
  const connectors = useAdminOrgConnectors(org.id);
  const usage = useAdminOrgUsage(org.id);

  const memberCount = members.data?.length ?? null;
  const licensedConnectors = connectors.data?.filter((c) => c.is_enabled).length ?? null;

  return (
    <div className="flex flex-col gap-5">
      <OrgSetupChecklist orgId={org.id} memberCount={memberCount} />

      {/* KPI grid */}
      <section
        aria-label="Statistiche organizzazione"
        className="grid grid-cols-2 gap-3 md:grid-cols-4"
      >
        <StatCard
          icon={<Users size={14} strokeWidth={1.75} />}
          label="Membri"
          value={memberCount ?? "—"}
          hint={org.max_members === -1 ? "Posti illimitati" : `su ${org.max_members} posti`}
        />
        <StatCard
          icon={<Globe size={14} strokeWidth={1.75} />}
          label="Connector attivi"
          value={licensedConnectors ?? "—"}
        />
        <StatCard
          icon={<Zap size={14} strokeWidth={1.75} />}
          label="Multiplier"
          value={`×${org.usage_multiplier}`}
          hint={
            // ``cost_cap_eur`` is declared ``number`` on the wire but
            // the BE omits the field for placeholder / multiplier=0 orgs
            // (e.g. the platform-seed ``00000000-…`` row). Guarding on
            // ``Number.isFinite`` avoids the ``Cannot read properties of
            // undefined (reading 'toFixed')`` crash that took the whole
            // detail route down on June 5, 2026.
            usage.data && Number.isFinite(usage.data.cost_cap_eur)
              ? `cap €${usage.data.cost_cap_eur.toFixed(0)}/mese`
              : undefined
          }
        />
        <StatCard
          icon={<Building2 size={14} strokeWidth={1.75} />}
          label="Deployment"
          value={<span className="capitalize">{org.deployment_mode || "shared"}</span>}
        />
      </section>

      {/* Canonical facts */}
      <section
        aria-labelledby="org-facts"
        className="rounded-lg border border-hairline bg-card/55 p-5"
      >
        <h2
          id="org-facts"
          className="mb-4 font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
        >
          Dettagli
        </h2>
        <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          <Field
            icon={<Hash size={11} aria-hidden />}
            label="Slug"
            value={<span className="font-mono">{org.slug}</span>}
          />
          <Field
            icon={<Mail size={11} aria-hidden />}
            label="Email fatturazione"
            value={org.billing_email || <span className="text-ink-3 italic">Non impostata</span>}
          />
          <Field
            label="Tier"
            value={
              <Badge variant="accent" className="font-mono text-[11px]">
                ×{org.usage_multiplier}
              </Badge>
            }
          />
          <Field
            label="Max membri"
            value={
              org.max_members === -1 ? (
                <span className="inline-flex items-center gap-1 text-ink-1">
                  <InfinityIcon size={13} aria-hidden /> Illimitato
                </span>
              ) : (
                org.max_members
              )
            }
          />
          <Field
            label="Stato"
            value={
              <span className={org.is_active ? "text-success-foreground" : "text-destructive"}>
                {org.is_active ? "Attiva" : "Disattivata"}
              </span>
            }
          />
          <Field
            icon={<Calendar size={11} aria-hidden />}
            label="Creata"
            value={new Date(org.created_at).toLocaleDateString("it-IT", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          />
          <Field
            icon={<Calendar size={11} aria-hidden />}
            label="Ultima modifica"
            value={new Date(org.updated_at).toLocaleDateString("it-IT", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          />
        </dl>
      </section>
    </div>
  );
}
